import { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Spin } from "antd";
import PowerBIComponent from "base/control/report/PowerBIComponent";
import { canAccessDashboard, isDashboardInMenu } from "utils";
import { getDashboardFilters } from "./getDashboardFilters";
import { useGetReport } from "./hooks/useGetReport/useGetReport";
import styles from "./index.module.scss";

/**
 * Power BI dashboard page.
 *
 * Access is encoded upstream: role-eligible users satisfy `canAccessDashboard()`
 * (derived synchronously from the login payload), and PO/PPO users are reflected
 * by the backend-trimmed menu via `isDashboardInMenu()`. A user who deep-links
 * without either signal is redirected to the no-permission page.
 */
const PowerBiDashboard = () => {
  const history = useHistory();
  const hasAccess = canAccessDashboard() || isDashboardInMenu();

  useEffect(() => {
    if (!hasAccess) {
      history.replace("/NoPermissionPage/noAccess");
    }
  }, [hasAccess, history]);

  const { config, refresh } = useGetReport(hasAccess);
  const filters = useMemo(() => getDashboardFilters(), []);

  if (!hasAccess) {
    return null;
  }

  return (
    <div className={styles.powerBiDashboard} data-testid="powerbi-dashboard-page">
      {/*
        Gated on `!config` alone, not `loading`: ahooks keeps the previous
        config in place while `refresh()` is in flight, so a background token
        refresh (proactive or after ClientError_TokenExpired) never unmounts
        the already-embedded report. Only the very first load, before any
        config has arrived, shows the spinner.
      */}
      {!config ? (
        <div className={styles.powerBiDashboardLoading} data-testid="powerbi-dashboard-loading">
          <Spin size="large" />
        </div>
      ) : (
        <PowerBIComponent
          embedUrl={config.embedUrl}
          accessToken={config.embedToken}
          reportId={config.reportId}
          expiration={config.expiration}
          filters={filters}
          onTokenExpired={refresh}
        />
      )}
    </div>
  );
};

export default PowerBiDashboard;
